import { useCallback, useMemo, useRef, useState } from "react";

import { usePortalDropdown } from "./dropdown-hooks";

export type LabeledItem<T> = {
  id: T;
  name: string;
  code?: string | null;
};

/**
 * Shared selection logic for small labeled-entity dropdowns (routes, shipping
 * addresses, …). Handles search state, item list composition with a "none"
 * row, selection + close, and portal-dropdown wiring.
 */
export function useLabeledSelect<T extends string>(params: {
  items: LabeledItem<T>[];
  value: T | null;
  onChange: (id: T | null) => void;
  noneLabel: string;
  width?: number;
}) {
  const { items, value, onChange, noneLabel, width = 220 } = params;
  const [search, setSearch] = useState("");

  const selected = useMemo(
    () => items.find((i) => i.id === value),
    [items, value],
  );

  const displayLabel = formatItemLabel(selected, noneLabel);

  const entries = useMemo(() => {
    const list: Array<{ id: T | null; label: string; sublabel?: string }> = [
      { id: null, label: noneLabel },
    ];
    const needle = search.toLowerCase().trim();
    for (const item of items) {
      const label = formatItemLabel(item, noneLabel);
      if (needle && !label.toLowerCase().includes(needle)) continue;
      list.push({ id: item.id, label, sublabel: item.code ?? undefined });
    }
    return list;
  }, [items, noneLabel, search]);

  const setOpenRef = useRef<(v: boolean) => void>(() => {});

  const onSelectIndex = useCallback(
    (index: number) => {
      const entry = entries[index];
      if (!entry) return;
      onChange(entry.id);
      setOpenRef.current(false);
    },
    [entries, onChange],
  );

  const resetSearch = useCallback(() => setSearch(""), []);

  const dd = usePortalDropdown({
    itemCount: entries.length,
    onSelect: onSelectIndex,
    width,
    resetOnClose: [resetSearch],
  });
  setOpenRef.current = dd.setOpen;

  return { search, setSearch, displayLabel, items: entries, dd };
}

function formatItemLabel<T>(
  item: LabeledItem<T> | undefined,
  fallback: string,
): string {
  if (!item) return fallback;
  return item.code ? `${item.code} - ${item.name}` : item.name;
}
